/**
 * Create / edit dialog for a promo code.
 *
 * Create mode sends a full PromoCodeCreate. Edit mode sends a PromoCodeUpdate
 * that MUST NOT include `code` (backend forbids extra fields).
 */

import { useEffect, useState } from 'react';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/cn';
import { Button, Input, Select, Textarea } from '@/components/ui';
import { HttpError } from '@/lib/api/client';
import { parseBackendDateTime } from '@/lib/format/datetime';
import {
  useCreatePromoCodeMutation,
  useUpdatePromoCodeMutation,
} from '@/lib/queries/usePromoCodes';
import type {
  PromoCodeResponse,
  PromoCodeAudience,
  PromoCodeStatus,
  PromoCodeCreate,
  PromoCodeUpdate,
} from '@/lib/api/promoCodes';

interface PromoCodeFormDialogProps {
  open: boolean;
  editing: PromoCodeResponse | null;
  onClose: () => void;
}

interface FormState {
  code: string;
  free_days: string;
  audience: PromoCodeAudience;
  new_user_window_days: string;
  redeem_start_at: string;
  redeem_end_at: string;
  max_uses: string;
  description: string;
  status: PromoCodeStatus;
}

/** Convert an ISO backend datetime to a value usable by <input type="datetime-local">. */
function isoToLocalInput(iso: string): string {
  const d = parseBackendDateTime(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Convert a datetime-local value (local time) to an ISO UTC string. */
function localInputToIso(value: string): string {
  return new Date(value).toISOString();
}

function emptyForm(): FormState {
  return {
    code: '',
    free_days: '7',
    audience: 'all_users',
    new_user_window_days: '',
    redeem_start_at: '',
    redeem_end_at: '',
    max_uses: '',
    description: '',
    status: 'active',
  };
}

export function PromoCodeFormDialog({ open, editing, onClose }: PromoCodeFormDialogProps) {
  const { messages } = useI18n();
  const t = messages.promoCodesPage;

  const createMutation = useCreatePromoCodeMutation();
  const updateMutation = useUpdatePromoCodeMutation();

  const [form, setForm] = useState<FormState>(emptyForm());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrorMessage(null);
    createMutation.reset();
    updateMutation.reset();
    if (editing) {
      setForm({
        code: editing.code,
        free_days: String(editing.free_days),
        audience: editing.audience,
        new_user_window_days:
          editing.new_user_window_days != null ? String(editing.new_user_window_days) : '',
        redeem_start_at: isoToLocalInput(editing.redeem_start_at),
        redeem_end_at: isoToLocalInput(editing.redeem_end_at),
        max_uses: editing.max_uses != null ? String(editing.max_uses) : '',
        description: editing.description ?? '',
        status: editing.status,
      });
    } else {
      setForm(emptyForm());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  if (!open) return null;

  const audienceOptions = [
    { value: 'all_users', label: t.audienceAllUsers },
    { value: 'never_subscribed', label: t.audienceNeverSubscribed },
    { value: 'new_users', label: t.audienceNewUsers },
  ];

  const statusOptions = [
    { value: 'active', label: t.statusActive },
    { value: 'paused', label: t.statusPaused },
    { value: 'archived', label: t.statusArchived },
  ];

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const parseOptionalInt = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const n = parseInt(trimmed, 10);
    return Number.isNaN(n) ? null : n;
  };

  const handleSave = async () => {
    setErrorMessage(null);

    const newUserWindow = parseOptionalInt(form.new_user_window_days);
    const maxUses = parseOptionalInt(form.max_uses);
    const freeDays = parseInt(form.free_days, 10);
    const description = form.description.trim() === '' ? null : form.description.trim();

    try {
      if (editing) {
        const updateData: PromoCodeUpdate = {
          free_days: freeDays,
          audience: form.audience,
          new_user_window_days: newUserWindow,
          redeem_start_at: localInputToIso(form.redeem_start_at),
          redeem_end_at: localInputToIso(form.redeem_end_at),
          max_uses: maxUses,
          status: form.status,
          description,
        };
        await updateMutation.mutateAsync({ id: editing.id, data: updateData });
      } else {
        const createData: PromoCodeCreate = {
          code: form.code.trim(),
          free_days: freeDays,
          audience: form.audience,
          new_user_window_days: newUserWindow,
          redeem_start_at: localInputToIso(form.redeem_start_at),
          redeem_end_at: localInputToIso(form.redeem_end_at),
          max_uses: maxUses,
          description,
        };
        await createMutation.mutateAsync(createData);
      }
      onClose();
    } catch (err) {
      const httpErr = err as HttpError;
      if (httpErr.statusCode === 409) {
        setErrorMessage(t.conflictError);
      } else {
        setErrorMessage(httpErr.message || t.loadError);
      }
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const showNewUserWindow = form.audience === 'new_users';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card text-card-foreground rounded-lg border border-border shadow-elevation-1 p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {editing ? t.editTitle : t.createTitle}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Input
              label={t.fieldCode}
              required
              value={form.code}
              disabled={!!editing}
              helperText={t.codeHint}
              onChange={(e) => setField('code', e.target.value)}
            />
          </div>

          <Input
            label={t.fieldFreeDays}
            type="number"
            min={1}
            required
            value={form.free_days}
            onChange={(e) => setField('free_days', e.target.value)}
          />

          <Select
            label={t.fieldAudience}
            value={form.audience}
            options={audienceOptions}
            onChange={(e) => setField('audience', e.target.value as PromoCodeAudience)}
          />

          {showNewUserWindow && (
            <Input
              label={t.fieldNewUserWindowDays}
              type="number"
              min={1}
              value={form.new_user_window_days}
              helperText={t.newUserWindowHint}
              onChange={(e) => setField('new_user_window_days', e.target.value)}
            />
          )}

          <Input
            label={t.fieldMaxUses}
            type="number"
            min={1}
            value={form.max_uses}
            helperText={t.maxUsesHint}
            onChange={(e) => setField('max_uses', e.target.value)}
          />

          <Input
            label={t.fieldRedeemStartAt}
            type="datetime-local"
            required
            value={form.redeem_start_at}
            onChange={(e) => setField('redeem_start_at', e.target.value)}
          />

          <Input
            label={t.fieldRedeemEndAt}
            type="datetime-local"
            required
            value={form.redeem_end_at}
            onChange={(e) => setField('redeem_end_at', e.target.value)}
          />

          {editing && (
            <Select
              label={t.fieldStatus}
              value={form.status}
              options={statusOptions}
              onChange={(e) => setField('status', e.target.value as PromoCodeStatus)}
            />
          )}

          <div className="sm:col-span-2">
            <Textarea
              label={t.fieldDescription}
              value={form.description}
              rows={2}
              onChange={(e) => setField('description', e.target.value)}
            />
          </div>
        </div>

        {errorMessage && (
          <p className={cn('mt-4 text-sm text-destructive')}>{errorMessage}</p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t.cancel}
          </Button>
          <Button onClick={handleSave} loading={isSaving}>
            {isSaving ? t.saving : t.save}
          </Button>
        </div>
      </div>
    </div>
  );
}
